import { useEffect, useState } from "react";
import { subscribeDialogs, resolveDialog, type DialogRequest } from "../lib/dialog";

// Renders the queued alert/confirm dialogs as styled in-app modals (matching the
// audit preflight dialog). Mount once near the app root.
export function DialogHost() {
  const [reqs, setReqs] = useState<DialogRequest[]>([]);
  const [input, setInput] = useState("");
  // Track the active theme so the dialog matches Light/Dark mode (reacts to live toggles).
  const [isLight, setIsLight] = useState(() => document.documentElement.getAttribute("data-theme") === "light");
  useEffect(() => subscribeDialogs(setReqs), []);
  useEffect(() => {
    const sync = () => setIsLight(document.documentElement.getAttribute("data-theme") === "light");
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const req = reqs[0];
  // Seed the input with the prompt's default value whenever the active request changes.
  useEffect(() => {
    if (req?.kind === "prompt") setInput(req.defaultValue ?? "");
  }, [req?.id, req?.kind, req?.defaultValue]);

  if (!req) return null;

  const accent =
    req.tone === "danger" ? "#dc2626" :
    req.tone === "create" ? "#0f766e" :
    req.tone === "info"   ? "#0e7490" :
    "#1d4ed8";

  const c = isLight
    ? {
        backdrop: "rgba(15,23,42,.35)", panelBg: "#ffffff", panelBorder: "1px solid #e2e8f0",
        shadow: "0 16px 48px rgba(15,23,42,.18)", title: "#0f172a", msg: "#334155",
        inputBg: "#f8fafc", inputBorder: "1px solid #cbd5e1", inputColor: "#0f172a",
        cancelBorder: "1px solid #cbd5e1", cancelColor: "#475569",
      }
    : {
        backdrop: "rgba(2,6,23,.6)", panelBg: "#0f172a", panelBorder: "1px solid rgba(255,255,255,.12)",
        shadow: "0 16px 48px rgba(0,0,0,.55)", title: "#f1f5f9", msg: "rgba(255,255,255,.82)",
        inputBg: "rgba(255,255,255,.06)", inputBorder: "1px solid rgba(255,255,255,.2)", inputColor: "#f1f5f9",
        cancelBorder: "1px solid rgba(255,255,255,.15)", cancelColor: "rgba(255,255,255,.6)",
      };

  const dismiss = (value: boolean | string | null) => resolveDialog(req.id, value);
  // Confirming a prompt always submits the (trimmed) value, INCLUDING an empty string — only
  // Cancel / backdrop / Esc return null. This lets callers act on a deliberately-cleared field
  // (e.g. the Edit Link dialog: clearing the URL and saving removes the link, keeping its text).
  const confirmPrompt = () => dismiss(input.trim());
  // Clicking the backdrop or Esc = cancel for confirm/prompt, acknowledge for alert.
  const onBackdrop = () => dismiss(req.kind === "alert" ? true : req.kind === "prompt" ? null : false);

  return (
    <div
      onClick={onBackdrop}
      style={{
        position: "fixed", inset: 0, background: c.backdrop, backdropFilter: "blur(2px)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: c.panelBg, border: c.panelBorder, borderRadius: 10,
          padding: "22px 24px", maxWidth: 480, width: "90%", boxShadow: c.shadow,
          fontFamily: "Mulish, sans-serif",
        }}
      >
        {req.title && (
          <div style={{ fontFamily: "'Oswald', 'Barlow Condensed', 'Inter', sans-serif", fontSize: 17, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px", color: c.title, marginBottom: 12 }}>{req.title}</div>
        )}
        {req.messageHtml ? (
          <p
            style={{ fontSize: 13, lineHeight: 1.55, color: c.msg, margin: req.kind === "prompt" ? "0 0 12px" : "0 0 20px" }}
            dangerouslySetInnerHTML={{ __html: req.messageHtml }}
          />
        ) : (
          <p style={{ fontSize: 13, lineHeight: 1.55, color: c.msg, margin: req.kind === "prompt" ? "0 0 12px" : "0 0 20px", whiteSpace: "pre-wrap" }}>
            {req.message}
          </p>
        )}
        {req.kind === "prompt" && (
          <input
            autoFocus
            value={input}
            placeholder={req.placeholder}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") { e.preventDefault(); confirmPrompt(); }
              else if (e.key === "Escape") { e.preventDefault(); dismiss(null); }
            }}
            style={{
              width: "100%", boxSizing: "border-box", margin: "0 0 20px", padding: "9px 12px",
              borderRadius: 6, border: c.inputBorder, background: c.inputBg,
              color: c.inputColor, fontFamily: "inherit", fontSize: 13, outline: "none",
            }}
          />
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          {(req.kind === "confirm" || req.kind === "prompt") && (
            <button
              onClick={() => dismiss(req.kind === "prompt" ? null : false)}
              style={{
                padding: "8px 18px", borderRadius: 0, border: c.cancelBorder,
                background: "transparent", color: c.cancelColor, cursor: "pointer",
                fontFamily: "inherit", fontSize: 12, fontWeight: 600,
              }}
            >
              {req.cancelLabel ?? "Cancel"}
            </button>
          )}
          <button
            autoFocus={req.kind !== "prompt"}
            onClick={() => (req.kind === "prompt" ? confirmPrompt() : dismiss(true))}
            style={{
              padding: "8px 22px", borderRadius: 0, border: "none",
              background: req.tone === "danger" ? accent : "#16a34a",
              color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700,
            }}
          >
            {req.confirmLabel ?? (req.kind === "alert" ? "OK" : req.kind === "prompt" ? "Add Link" : "Continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
